'use client';
import { useRouter } from 'next/navigation';
import { useState } from 'react';
import { createCheckoutSession } from 'services/incorporationApi';
import { encryptPassword } from 'utils/encryption';
import PosthogEvent from 'lib/posthogEvents';
import theraToast from 'components/generic-components/toast/TheraToast';

export default function PaymentInfo({ formik, totalAmount = 0, items = [], password }) {
  const [isLoading, setIsLoading] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('stripe');
  const router = useRouter();

  const handleStripeCheckout = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    // Prevent any parent form submission
    const form = event.target.closest('form');
    if (form) {
      // Temporarily disable the form
      form.onsubmit = (e) => {
        e.preventDefault();
        return false;
      };
    }

    setIsLoading(true);

    try {
      const origin = window.location.origin;

      // Handle address - could be string or object
      const addressData = formik?.values?.userPersonalAddress;
      let addressMetadata = {};

      if (typeof addressData === 'object' && addressData !== null) {
        // Address is an object with line1, line2, city, state, postalCode
        addressMetadata = {
          addressLine1: addressData.line1 || '',
          addressLine2: addressData.line2 || '',
          addressCity: addressData.city || '',
          addressState: addressData.state || '',
          addressPostalCode: addressData.postalCode || '',
        };
      } else {
        // Address is a string or null
        addressMetadata = {
          address: addressData || '',
        };
      }

      let encryptedUserId = '';
      try {
        if (password?.trim()) {
          encryptedUserId = encryptPassword(password);
        }
      } catch (encryptionError) {
        console.error('Password encryption failed:', encryptionError);
        alert('There was an issue processing your account information. Please try again.');
        setIsLoading(false);
        return;
      }

      // Prepare incorporation data from form values
      const incorporationData = {
        businessName: formik?.values?.businessName || '',
        package: formik?.values?.package || '',
        email: formik?.values?.email || '',
        state: formik?.values?.state || '',
        firstName: formik?.values?.firstName || '',
        lastName: formik?.values?.lastName || '',
        phone: formik?.values?.phone || '',
        experience: formik?.values?.experience || '',
        progress: formik?.values?.progress || '',
        industry: formik?.values?.industry || '',
        agent: formik?.values?.agent || '',
        dbaService: String(formik?.values?.fasterFiling || false),
        worryFreeCompliance: String(formik?.values?.worryFreeCompliance || false),
        einDocuments: JSON.stringify(formik?.values?.einDocuments || []),
        tradeMarkCompany: String(formik?.values?.operatingAgreement || false),
        user_id: encryptedUserId,
        ...addressMetadata, // Spread the address fields
      };

      if (formik?.values?.email) {
        PosthogEvent.captureIncorporationCheckoutCreated({
          email: formik.values.email,
        });
      }

      const response = await createCheckoutSession({
        amount: totalAmount, // Amount in dollars
        productName: 'Business Incorporation Payment',
        currency: 'usd',
        successUrl: `${origin}/start/success?session_id={CHECKOUT_SESSION_ID}&package=${formik?.values?.package}`,
        cancelUrl: `${origin}/start/cancel?session_id={CHECKOUT_SESSION_ID}`,
        metadata: {
          ...incorporationData,
          items: JSON.stringify(items),
        },
      });

      if (!response.checkoutUrl) {
        throw new Error(
          'Unable to create checkout session. Please try again or contact support if the issue persists.',
        );
      }
      router.push(response.checkoutUrl);
    } catch (err) {
      theraToast.error({
        message: 'There was an issue processing your account information. Please try again.',
        error: err,
        options: {
          id: 'failed-to-process-account-information',
        },
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-6 text-dark">
      {/* Payment Method Selection */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold">Select Payment Method</h3>
        <div className="flex gap-4">
          <div
            className={`flex flex-1 cursor-pointer flex-col items-start gap-3 rounded-lg border-2 px-4 py-3 ${
              paymentMethod === 'stripe' ? 'border-[#0339f8] bg-white' : 'border-2 border-[#d1d1d1] bg-[#F9FAFB]'
            }`}
            onClick={() => setPaymentMethod('stripe')}
          >
            <div className="flex gap-2">
              <div className="flex w-10 items-center rounded-sm border border-[#d1d1d1] p-[2px]">
                <img src="/images/thera-start/visa.png" alt="Visa" className="w-full" />
              </div>
              <div className="flex w-10 items-center rounded-sm border border-[#d1d1d1] p-[2px]">
                <img src="/images/thera-start/mastercard.png" alt="Mastercard" className="w-full" />
              </div>
              <div className="flex w-10 items-center rounded-sm border border-[#d1d1d1] p-[2px]">
                <img src="/images/thera-start/amex.png" alt="Amex" className="w-full" />
              </div>
            </div>
            <span className="ml-2 text-base font-medium text-black">Credit or debit card</span>
          </div>
          {/* <div
            className={`flex-1 flex-col border-2 rounded-lg px-4 py-3 flex items-start gap-3 cursor-pointer ${paymentMethod === "apple pay" ? "border-[#0339f8]" : "border-2 border-[#d1d1d1] bg-[#F9FAFB]"
              }`}
            onClick={() => setPaymentMethod("apple pay")}
          >
            <div className='w-12 flex items-center p-[4px] border border-[#d1d1d1] rounded-sm'>
              <img src={apple.src} alt='Apple Pay' className='w-full' />
            </div>
            <span className='ml-2 text-base font-medium text-black'>Apple Pay</span>
          </div> */}
        </div>
      </div>

      {/* Payment Buttons */}
      {paymentMethod === 'stripe' ? (
        <button
          type="button"
          onClick={(e) => handleStripeCheckout(e)}
          disabled={isLoading}
          className="mt-4 w-full rounded-lg bg-black py-2 text-white hover:bg-gray-800 disabled:bg-gray-400"
        >
          {isLoading ? 'Processing...' : 'Continue'}
        </button>
      ) : (
        <div className="w-full">
          {/* <Suspense fallback={<PayPalLoading />}>
            {typeof window !== 'undefined' && (
              <PayPalScriptProvider
                options={{
                  'client-id': process.env.NEXT_PUBLIC_PAYPAL_CLIENT_ID,
                  currency: 'USD',
                }}
                deferLoading={true}
              >
                <PayPalButtons
                  style={{ layout: 'vertical' }}
                  createOrder={(data, actions) => {
                    return actions.order.create({
                      purchase_units: [
                        {
                          amount: {
                            value: totalAmount.toString(),
                            currency_code: 'USD',
                          },
                          description: `${formik?.values?.package === 'pro' ? 'Pro' : 'Starter'} Package + CA State Filing Fee`,
                        },
                      ],
                    });
                  }}
                  onApprove={handlePayPalSuccess}
                  onError={(err) => {
                    console.error('PayPal Error:', err);
                    alert('PayPal payment failed. Please try again or use a different payment method.');
                  }}
                />
              </PayPalScriptProvider>
            )}
          </Suspense> */}
        </div>
      )}
    </div>
  );
}
